import {
  parseFlyAuthWhoami,
  parseGcloudAuthList,
  parseGithubAuthStatus,
  parseSupabaseProjectsList,
  parseVercelWhoami,
} from "./services-parse.ts";

/**
 * The machine-connected services catalog (docs/specs/machine-connected-services.md §3, §4, §11 S1): five CLIs whose
 * sign-in state a node may report back to the hub. Everything here is hub-owned. A node names a service; it never
 * supplies argv, a host, a parser or fix text.
 *
 * Probes are read-only and never print or move a credential: no `--show-token`, no `print-access-token`, no login verb,
 * no `env pull`. `findForbiddenProbeArg` enforces that over every catalog entry in the tests.
 *
 * Sources for each probe are the official CLI docs cited on the entry (docs URLs checked when this was written).
 */

export const SERVICE_NAMES = ["github", "vercel", "supabase", "fly", "gcloud"] as const;
export type ServiceName = (typeof SERVICE_NAMES)[number];

export const SERVICE_STATES = ["connected", "not_logged_in", "not_installed", "unknown"] as const;
export type ServiceState = (typeof SERVICE_STATES)[number];

/** The sign-in hosts the hub knows. `fixFor` accepts only these; any other value is ignored, never echoed. */
export const SERVICE_HOSTS = ["github.com", "vercel.com", "supabase.com", "fly.io", "cloud.google.com"] as const;
export type ServiceHost = (typeof SERVICE_HOSTS)[number];

/** What a probe run looked like from the outside. `spawnError` is set when the process never produced an exit code. */
export type ProbeRun = {
  exitCode: number | null;
  stdout: string;
  stderr: string;
  spawnError?: "ENOENT" | "ETIMEDOUT" | "OTHER" | null;
};

/** A parser's answer. `account` is set only for a connected service whose account name passed the token and shape checks. */
export type ServiceReading = { state: ServiceState; account: string | null };

export type ServiceCapability = {
  name: ServiceName;
  label: string;
  /** The binary to run. Probes run with `shell: false`, so argv is an array and never a string. */
  bin: string;
  /** Read-only probe argv (no binary). Checked by `findForbiddenProbeArg`. */
  probeArgv: readonly string[];
  /** Hosts this service signs in to; the first is the default. */
  hosts: readonly ServiceHost[];
  /** The CLI docs and source the probe was checked against. */
  sources: readonly string[];
  /** What the probe can and cannot observe; shown to people in the catalog, never to a node. */
  note: string;
  parse(run: ProbeRun): ServiceReading;
};

export const SERVICE_CATALOG: Readonly<Record<ServiceName, ServiceCapability>> = Object.freeze({
  github: {
    name: "github",
    label: "GitHub CLI",
    bin: "gh",
    probeArgv: ["auth", "status", "--hostname", "github.com"],
    hosts: ["github.com"],
    sources: ["https://cli.github.com/manual/gh_auth_status", "https://github.com/cli/cli/blob/trunk/pkg/cmd/auth/status/status.go"],
    note: "gh validates the token against the API, so an offline run reads as unknown.",
    parse: parseGithubAuthStatus,
  },
  vercel: {
    name: "vercel",
    label: "Vercel CLI",
    bin: "vercel",
    probeArgv: ["whoami", "--non-interactive"],
    hosts: ["vercel.com"],
    sources: ["https://vercel.com/docs/cli/whoami", "https://vercel.com/docs/cli/global-options"],
    note: "whoami prints the username; the signed-out path exits 1 with \"Logged out.\".",
    parse: parseVercelWhoami,
  },
  supabase: {
    name: "supabase",
    label: "Supabase CLI",
    bin: "supabase",
    probeArgv: ["projects", "list", "-o", "json"],
    hosts: ["supabase.com"],
    sources: ["https://supabase.com/docs/reference/cli/supabase-projects-list", "https://supabase.com/docs/reference/cli/supabase-login"],
    note: "The CLI has no whoami. A JSON project list means the stored token works; the list itself is never kept.",
    parse: parseSupabaseProjectsList,
  },
  fly: {
    name: "fly",
    label: "Fly CLI (flyctl)",
    bin: "fly",
    probeArgv: ["auth", "whoami"],
    hosts: ["fly.io"],
    sources: ["https://docs.fly.io/flyctl/auth-whoami"],
    note: "whoami prints the signed-in email; the signed-out message is quoted from flyctl, not from the docs page.",
    parse: parseFlyAuthWhoami,
  },
  gcloud: {
    name: "gcloud",
    label: "Google Cloud SDK (gcloud)",
    bin: "gcloud",
    probeArgv: ["auth", "list", "--filter=status:ACTIVE", "--format=value(account)"],
    hosts: ["cloud.google.com"],
    sources: ["https://docs.cloud.google.com/sdk/gcloud/reference/auth/list"],
    note: "Reads local gcloud configuration only, so it works offline and never reports unknown for lack of network.",
    parse: parseGcloudAuthList,
  },
} satisfies Record<ServiceName, ServiceCapability>);

// Object.freeze is shallow: freeze each probe argv too, so runtime code cannot widen a probe after the check.
for (const svc of Object.values(SERVICE_CATALOG)) Object.freeze(svc.probeArgv);

export function serviceCapability(name: string): ServiceCapability | null {
  return Object.hasOwn(SERVICE_CATALOG, name) ? SERVICE_CATALOG[name as ServiceName] : null;
}

/**
 * The first forbidden element in a probe's argv, or null. Matches whole argv elements, never substrings, so the fix
 * text may say `gh auth login` without tripping the check (that text is never a probe argv). Rules:
 *   --show-token (and -t, gh's short form), print-access-token, `token` (gh auth token, fly auth token),
 *   the login verb, and `env pull` as adjacent elements.
 */
export function findForbiddenProbeArg(argv: readonly string[]): string | null {
  for (let i = 0; i < argv.length; i++) {
    const el = argv[i] ?? "";
    if (el === "--show-token" || el.startsWith("--show-token=") || el === "-t" || el.startsWith("-t=")) return "--show-token";
    if (el === "--token" || el.startsWith("--token=")) return "--token";
    if (el === "print-access-token" || el === "print-identity-token") return el;
    if (el === "token") return "token";
    if (el === "login" || el === "logout") return el;
    if (el === "env" && argv[i + 1] === "pull") return "env pull";
  }
  return null;
}

/**
 * Hub-owned fix text for a state, per service. Every string is a literal in this file; the only interpolation is a
 * catalog host, and only after `fixFor` has checked it against the closed `SERVICE_HOSTS` list. Connected needs no fix.
 */
const FIX: Readonly<Record<ServiceName, Partial<Record<ServiceState, (host: ServiceHost) => string>>>> = {
  github: {
    not_installed: () => "Install the GitHub CLI from https://cli.github.com/, then run `gh auth login --hostname github.com` on this machine.",
    not_logged_in: (h) => `Run \`gh auth login --hostname ${h}\` on this machine, then check again.`,
  },
  vercel: {
    not_installed: () => "Install the Vercel CLI (https://vercel.com/docs/cli), then run `vercel login` on this machine.",
    not_logged_in: () => "Run `vercel login` on this machine, then check again.",
  },
  supabase: {
    not_installed: () => "Install the Supabase CLI (https://supabase.com/docs/guides/cli), then run `supabase login` on this machine.",
    not_logged_in: () => "Run `supabase login` on this machine, then check again.",
  },
  fly: {
    not_installed: () => "Install flyctl (https://fly.io/docs/flyctl/install/), then run `fly auth login` on this machine.",
    not_logged_in: () => "Run `fly auth login` on this machine, then check again.",
  },
  gcloud: {
    not_installed: () => "Install the Google Cloud SDK (https://cloud.google.com/sdk/docs/install), then run `gcloud auth login` on this machine.",
    not_logged_in: () => "Run `gcloud auth login` on this machine, then check again.",
  },
};

/**
 * GitHub readiness as a node's check reports it (docs/specs/github-machine-cli.md §5.1). `connected` in the heartbeat's
 * `ServiceState` is `ready` here; `no_access` is repo-scoped (logged in, but no write permission on a project's repo), so it
 * rides the project's `gh_repo_access` result and never the host-level capability row.
 */
export const GITHUB_READY_STATES = ["ready", "not_installed", "not_logged_in", "no_access", "unknown"] as const;
export type GithubReadyState = (typeof GITHUB_READY_STATES)[number];

/**
 * The oldest `gh` Roomate's flows use (`--hostname` on auth status/api, `--json … --jq` on repo view, `gh pr merge
 * --match-head-commit`). The flag is in the cli.github.com manual and in Debian's gh 2.x man page of September 2022; 2.20.0
 * (Sept 2022) is the conservative floor until H3 pins the exact release. An older or unparsable-as-older `gh` reads `not_installed`.
 */
export const GH_MIN_VERSION = [2, 20, 0] as const;

/** True/false for a parsed `gh --version` first line ("gh version 2.40.1 (2024-01-01)"); null when no version can be read. */
export function ghVersionAtLeast(output: string, min: readonly number[] = GH_MIN_VERSION): boolean | null {
  const m = /gh version (\d+)\.(\d+)\.(\d+)/i.exec(output);
  if (!m) return null;
  const have = [Number(m[1]), Number(m[2]), Number(m[3])];
  for (let i = 0; i < 3; i++) {
    if (have[i]! !== (min[i] ?? 0)) return have[i]! > (min[i] ?? 0);
  }
  return true;
}

const REPO_FULL_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9._-]{1,100}$/;

/**
 * The catalog fix for a GitHub readiness case, or null when ready. `repo` is echoed only when it is a plain `owner/repo`;
 * `tooOld` swaps the install text for the upgrade text. Same rule as `fixFor`: no node-supplied string reaches the output
 * unless it passed a closed check.
 */
export function githubReadyFix(state: GithubReadyState, host: ServiceHost | null, opts: { repo?: string | null; tooOld?: boolean } = {}): string | null {
  if (state === "ready") return null;
  const h: ServiceHost = host !== null && SERVICE_CATALOG.github.hosts.includes(host) ? host : SERVICE_CATALOG.github.hosts[0]!;
  if (state === "no_access") {
    const repo = opts.repo && isGithubRepoFullName(opts.repo) ? opts.repo : null;
    return `This account is logged in but cannot push${repo ? ` to ${repo}` : ""}. Ask the repository owner for write access, or run \`gh auth login --hostname ${h}\` as an account that has it, then check again.`;
  }
  if (state === "not_installed" && opts.tooOld) return `Upgrade the GitHub CLI to ${GH_MIN_VERSION.join(".")} or newer (https://cli.github.com/), then check again.`;
  return fixFor("github", state, h);
}

/** A plain `owner/repo` (no `.` or `..` repo name): the only repo form that reaches a `gh` argv or fix text. */
export function isGithubRepoFullName(v: string): boolean {
  return REPO_FULL_NAME.test(v) && !v.endsWith("/.") && !v.endsWith("/..");
}

const UNKNOWN_FIX ="The check could not finish (no network, or the CLI failed). This is not a sign-out; check again when the machine is online.";

/**
 * The fix text for a service in a state, or null when none is needed. Takes only typed values: `host` is used only when
 * it is one of the service's own hosts, otherwise the default host is used. Unknown names and states give null.
 */
export function fixFor(name: ServiceName, state: ServiceState, host: ServiceHost | null): string | null {
  const svc = serviceCapability(name);
  if (!svc || state === "connected") return null;
  if (state === "unknown") return UNKNOWN_FIX;
  const h: ServiceHost = host !== null && svc.hosts.includes(host) ? host : svc.hosts[0]!;
  const line = FIX[svc.name][state];
  return line ? line(h) : null;
}
